import React, { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../../context/ThemeProvider";
import { GET_EXAM_DISPLAY_TITLE } from "../../../app_constant";
import { BundleContext } from "../../context/BundleProvider";
import { AuthContext } from "../../context/AuthProvider";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSun,
  faMoon,
  faUser,
  faEnvelope,
  faCrown,
  faStar,
  faSignOutAlt,
} from '@fortawesome/free-solid-svg-icons';
import logger from "../../utils/logger";
import { auth } from "../../api/config";
import { isGhostMode, getGhostEmail, clearGhostUid } from "../../api/ghostUid";
import { useNavigate } from "react-router-dom";

function Header(props) {
  const { theme, currentTheme } = useContext(ThemeContext);
  const { selectedCertificate } = useContext(BundleContext);
  const [appName, setAppName] = useState("Exam Prep");
  const [logo, setLogo] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);
  const navigate = useNavigate();

  const {
    isSubscribe
  } = useContext(AuthContext);

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await auth().signOut();
      
      // Preserve theme preference
      const theme = localStorage.getItem("Theme");
      localStorage.clear();
      if (theme) localStorage.setItem("Theme", theme);
      
      navigate("/");
    } catch (error) {
      logger.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    const fetchAppName = async () => {
      try {
        const displayTitle = await GET_EXAM_DISPLAY_TITLE(selectedCertificate);
        logger.log('[Header.useEffect] displayTitle:', displayTitle);
        setAppName(displayTitle);
      } catch (error) {
        logger.error('Error fetching exam display title:', error);
      }
    };
    fetchAppName();
  }, [selectedCertificate]);

  // Theme-based styles
  const [sidebarWidth, setSidebarWidth] = useState(0);

  useEffect(() => {
    // Responsive sidebar width: 240px for lg screens, 0 for mobile
    const updateSidebarWidth = () => {
      if (window.matchMedia('(min-width: 1024px)').matches) {
        setSidebarWidth(40);
      } else {
        setSidebarWidth(0);
      }
    };
    updateSidebarWidth();
    window.addEventListener('resize', updateSidebarWidth);
    return () => window.removeEventListener('resize', updateSidebarWidth);
  }, []);

  // Check if mobile banner is visible
  const [bannerOffset, setBannerOffset] = useState(0);
  
  useEffect(() => {
    const checkBannerVisibility = () => {
      const isDismissed = sessionStorage.getItem('mobileAppBannerDismissed');
      if (isDismissed === 'true') {
        setBannerOffset(0);
        return;
      }
      
      // Check if user is on iOS or Android
      const userAgent = navigator.userAgent || '';
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isAndroid = /Android/.test(userAgent);
      
      if (isIOS || isAndroid) {
        // Banner height is approximately 56px
        setBannerOffset(56);
      } else {
        setBannerOffset(0);
      }
    };
    
    checkBannerVisibility();
    // Check periodically in case banner is dismissed
    const interval = setInterval(checkBannerVisibility, 1000);
    return () => clearInterval(interval);
  }, []);

  const styles = {
    header: {
      background: theme?.background?.main,
      backdropFilter: 'blur(10px)',
      borderBottom: `1px solid ${theme?.primary?.gray?.[200]}`,
      position: 'sticky',
      top: `${bannerOffset}px`,
      zIndex: 500,
      padding: '1rem 1.5rem',
      marginLeft: sidebarWidth,
      transition: 'margin-left 0.3s, top 0.3s ease',
    },
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: '1.5rem',
      paddingRight: '1.5rem',
    },
    leftSection: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
    },
    logoContainer: {
      marginRight: '1rem',
      position: 'relative',
    },
    logo: {
      height: '2rem',
      width: 'auto',
      objectFit: 'contain',
      transition: 'all 0.3s ease',
    },
    titleContainer: {
      position: 'relative',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme?.primary?.blue?.[500] || '#1e293b',
      margin: 0,
      textShadow: currentTheme === 'dark' 
        ? `0 0 20px ${theme?.primary?.blue?.[500]}30`
        : '0px 2px 4px rgba(0, 0, 0, 0.1)',
    },
    subtitle: {
      fontSize: '0.875rem',
      color: theme?.text?.tertiary || '#94a3b8',
      fontWeight: '500',
      margin: '0.25rem 0 0 0',
    },
    rightSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    actionButton: (isHovered) => ({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      cursor: 'pointer',
      padding: '0.5rem',
      borderRadius: '0.5rem',
      background: isHovered 
        ? (theme?.dashboard?.card?.background || 'rgba(255, 255, 255, 0.1)')
        : 'transparent',
      border: isHovered 
        ? `1px solid ${theme?.primary?.blue?.[500]}30`
        : '1px solid transparent',
      transition: 'all 0.2s ease',
    }),
    buttonIcon: (isHovered) => ({
      width: '1.5rem',
      height: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: isHovered 
        ? theme?.primary?.blue?.[500]
        : theme?.text?.primary,
      fontSize: '1rem',
      transition: 'color 0.2s ease',
    }),
    buttonLabel: (isHovered) => ({
      fontSize: '0.75rem',
      fontWeight: '500',
      color: isHovered 
        ? theme?.primary?.blue?.[500]
        : theme?.text?.tertiary,
      marginTop: '0.25rem',
      textAlign: 'center',
      transition: 'color 0.2s ease',
    }),
    upgradeButton: (isHovered) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      border: '1px solid transparent',
      background: isHovered
        ? (theme?.primary?.blue?.[600] || '#2563eb')
        : (theme?.primary?.blue?.[500] || '#3b82f6'),
      color: 'white',
      fontWeight: '600',
      fontSize: '0.875rem',
      transition: 'all 0.2s ease',
      transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
      boxShadow: isHovered 
        ? '0 4px 12px rgba(59, 130, 246, 0.3)'
        : 'none',
    }),
    divider: {
      width: '1px',
      height: '2rem',
      backgroundColor: theme?.dashboard?.card?.border || 'rgba(226, 232, 240, 0.3)',
      margin: '0 0.5rem',
    },
  };

  // Simple button component with hover effects
  const ActionButton = ({ icon, label, onClick, ariaLabel }) => {
    const isHovered = hoveredButton === label;
    
    return (
      <button
        style={styles.actionButton(isHovered)}
        onMouseEnter={() => setHoveredButton(label)}
        onMouseLeave={() => setHoveredButton(null)}
        onClick={onClick}
        aria-label={ariaLabel || label}
      >
        <div style={styles.buttonIcon(isHovered)}>
          <FontAwesomeIcon icon={icon} />
        </div>
        <span style={styles.buttonLabel(isHovered)}>
          {label}
        </span>
      </button>
    );
  };

  // Simple upgrade button component with hover effects
  const UpgradeButton = () => {
    const isHovered = hoveredButton === 'upgrade';
    
    return (
      <button
        style={styles.upgradeButton(isHovered)}
        onMouseEnter={() => setHoveredButton('upgrade')}
        onMouseLeave={() => setHoveredButton(null)}
        onClick={props.onUpgrade}
        aria-label="Upgrade to Premium"
      >
        <FontAwesomeIcon icon={faCrown} />
        <span>Get Premium</span>
      </button>
    );
  };

  const handleExitGhostMode = () => {
    clearGhostUid();
    window.location.reload();
  };

  return (
    <>
    {isGhostMode() && (
      <div style={{
        background: '#FF6B35', padding: '0.5rem 1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: `${bannerOffset}px`, zIndex: 501,
        marginLeft: sidebarWidth, transition: 'margin-left 0.3s',
      }}>
        <span style={{ color: '#fff', fontWeight: '700', fontSize: '0.875rem' }}>
          GHOST MODE: {getGhostEmail()}
        </span>
        <button
          onClick={handleExitGhostMode}
          style={{
            background: '#fff', color: '#FF6B35', border: 'none',
            padding: '0.25rem 0.75rem', borderRadius: '0.25rem',
            fontWeight: '700', fontSize: '0.8rem', cursor: 'pointer',
          }}
        >
          Exit
        </button>
      </div>
    )}
    <div style={{
      ...styles.header,
      ...(isGhostMode() ? { top: `${bannerOffset + 40}px` } : {}),
    }}>
      <div style={styles.container}>
        {/* Left Section - Logo and App Title */}
        <div style={styles.leftSection}>
          {/* Logo */}
          {logo && (
            <div style={styles.logoContainer}>
              <img
                style={styles.logo}
                src={logo}
                alt="logo"
              />
            </div>
          )}

          {/* App Title */}
          <div style={styles.titleContainer}>
            <h1 style={styles.title}>
              {appName}
            </h1>
            <div style={styles.subtitle}>
              Certification Exam Preparation
            </div>
          </div>
        </div>

        {/* Right Section - Action Buttons */}
        <div style={styles.rightSection}>
          {/* Upgrade Button - Only shown for free users */}
          {!isSubscribe && props.onUpgrade && (
            <>
              <UpgradeButton />
              <div style={styles.divider} />
            </>
          )}

          {/* Contact Support */}
          <ActionButton
            icon={faEnvelope}
            label="Support"
            onClick={props.onContactSupport}
            ariaLabel="Contact Support"
          />
          
          {/* Sign Out Button */}
          <ActionButton
            icon={faSignOutAlt}
            label="Sign Out"
            onClick={handleSignOut}
            ariaLabel="Sign Out"
          />
          
          {props.onProfile && (
            <ActionButton
              icon={faUser}
              label="Profile"
              onClick={props.onProfile}
              ariaLabel="User Profile"
            />
          )}

          {/* Theme Toggle Switch */}
          {props.onThemeChange && (
            <div
              style={{
                width: '48px',
                height: '24px',
                backgroundColor: currentTheme === 'dark' ? '#374151' : '#D1D5DB',
                borderRadius: '12px',
                position: 'relative',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                padding: '2px',
                marginLeft: '12px'
              }}
              onClick={props.onThemeChange}
              className="flex-shrink-0"
              aria-label={currentTheme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}
            >
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: '#FFFFFF',
                borderRadius: '50%',
                position: 'absolute',
                left: currentTheme === 'dark' ? 'calc(100% - 22px)' : '2px',
                transition: 'left 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB',
              }}>
                <FontAwesomeIcon
                  icon={currentTheme === "light" ? faSun : faMoon}
                  className="text-xs"
                  style={{ 
                    color: props.theme?.primary?.blue?.[500] || '#3B82F6',
                    fontSize: '10px'
                  }}
                />
              </div>
            </div>
          )}

          {/* Profile Button */}
          
        </div>
      </div>
    </div>
    </>
  );
}

export default Header;